// src/pages/landing-builder/OptinFunnelBuilder.jsx
// ── 2-Step Funnel builder ─────────────────────────────────────────────────────
// Preview and Editor taken directly from the working old file.
// Preview shows Step 1 (option buttons) and Step 2 (countdown + CTA).
// Urgency popup is shown as a semi-transparent overlay in the preview.
import { useState } from 'react';
import { Button, Input } from '../../components/ui';
import {
  Palette, Type, List, Layers, MousePointerClick,
  Bell, Timer, MessageSquare, Plus, Trash2,
} from 'lucide-react';
import { ColorPicker } from './shared';

// ==================== OPTIN FUNNEL PREVIEW ====================
export const OptinFunnelPreview = ({ config, theme, previewTab = 'step1', onSetPreviewTab }) => {
  const optin = config.optin || {};
  const primaryColor = theme?.primaryColor || '#008751';
  const options = optin.options || [{ text: 'Option 1' }, { text: 'Option 2' }];

  // Derive internal step from previewTab for back-compat
  const previewStep = previewTab === 'step2' ? 2 : 1;
  const setPreviewStep = (s) => onSetPreviewTab?.(s === 2 ? 'step2' : 'step1');

  // Tabs to show
  const tabs = optin.flowType === '2step' || !optin.flowType
    ? [
        { id: 'step1', label: 'Step 1' },
        { id: 'step2', label: 'Step 2' },
        ...(optin.popupEnabled ? [{ id: 'popup', label: 'Popup' }] : []),
      ]
    : [
        { id: 'step1', label: 'Page' },
        ...(optin.popupEnabled ? [{ id: 'popup', label: 'Popup' }] : []),
      ];

  return (
    <div
      className="min-h-full relative"
      style={{ backgroundColor: optin.backgroundColor || '#7cb342', fontFamily: theme?.fontFamily || 'Segoe UI, sans-serif' }}
    >
      {/* Preview tab bar — replaces the old Step 1/Step 2 toggle */}
      {tabs.length > 1 && (
        <div className="sticky top-0 z-10 flex gap-1 bg-card/95 border-b border-border/60 px-3 py-2 shadow-sm">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => onSetPreviewTab?.(tab.id)}
              className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                previewTab === tab.id
                  ? 'bg-primary text-white shadow-sm'
                  : 'text-muted-foreground hover:bg-muted'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      )}

      {/* Main container */}
      <div className="max-w-md mx-auto py-8 px-4">
        <div className="rounded-lg shadow-lg p-6" style={{ backgroundColor: optin.containerBgColor || '#ffffff' }}>

          {/* Logo */}
          {optin.logoUrl && (
            <img src={optin.logoUrl} alt="Logo" className="max-w-[200px] h-auto mx-auto mb-4" />
          )}

          {/* ── STEP 1 ── */}
          {(previewStep === 1 || optin.flowType === '1step') && (
            <>
              <h1
                className="text-center font-bold mb-2"
                style={{ color: optin.titleColor || primaryColor, fontSize: optin.titleSize || '20px' }}
              >
                {optin.title || 'Your Title Here'}
              </h1>

              {optin.subtitle && (
                <p className="text-center mb-6 text-sm" style={{ color: optin.subtitleColor || '#333' }}>
                  {optin.subtitle}
                </p>
              )}

              {/* Option buttons (2-step) */}
              {(optin.flowType === '2step' || !optin.flowType) && (
                <div className="space-y-3">
                  {options.map((opt, i) => (
                    <button
                      key={i}
                      onClick={() => setPreviewStep(2)}
                      className="w-full p-4 text-lg font-bold rounded-lg border-2 transition-all hover:scale-[1.02]"
                      style={{
                        borderColor: primaryColor,
                        backgroundColor: 'white',
                        color: optin.optionTextColor || primaryColor,
                      }}
                    >
                      {opt.text || `Option ${i + 1}`}
                    </button>
                  ))}
                </div>
              )}

              {/* Single CTA (1-step) */}
              {optin.flowType === '1step' && (
                <button
                  className="w-full p-4 text-lg font-bold rounded-lg transition-all relative"
                  style={{ backgroundColor: primaryColor, color: optin.ctaTextColor || '#ffffff' }}
                >
                  {optin.ctaText || 'Continue'}
                  {optin.isTestCta && (
                    <span className="absolute -top-2 -right-2 bg-purple-500 text-white text-xs px-1.5 py-0.5 rounded-full">A/B</span>
                  )}
                </button>
              )}
            </>
          )}

          {/* ── STEP 2 ── */}
          {previewStep === 2 && (optin.flowType === '2step' || !optin.flowType) && (
            <>
              <button
                onClick={() => setPreviewStep(1)}
                className="flex items-center gap-1 text-sm mb-4 hover:underline"
                style={{ color: primaryColor }}
              >
                ← Back
              </button>

              <h1
                className="text-center font-bold mb-2"
                style={{ color: optin.titleColor || primaryColor, fontSize: optin.titleSize || '20px' }}
              >
                {optin.title || 'Great Choice!'}
              </h1>

              {optin.subtitle && (
                <p className="text-center mb-4 text-sm" style={{ color: optin.subtitleColor || '#333' }}>
                  {optin.subtitle}
                </p>
              )}

              {/* Selected badge */}
              <div
                className="text-center py-2 px-4 rounded-full text-sm mb-4"
                style={{ backgroundColor: `${primaryColor}15`, border: `1px solid ${primaryColor}`, color: primaryColor }}
              >
                You selected: {options[0]?.text || 'Option 1'}
              </div>

              {/* Countdown */}
              {optin.countdownEnabled && (
                <div className="text-center mb-4">
                  <p className="text-xs text-muted-foreground mb-1">Time Left:</p>
                  <div className="text-4xl font-bold" style={{ color: '#dc3545' }}>
                    00:{String(optin.countdownDuration || 30).padStart(2, '0')}
                  </div>
                </div>
              )}

              <p className="text-center text-sm text-muted-foreground mb-4">
                Your access is confirmed. Continue below.
              </p>

              {/* Final CTA */}
              <button
                className="w-full p-4 text-lg font-bold rounded-lg transition-all relative"
                style={{ backgroundColor: primaryColor, color: optin.ctaTextColor || '#ffffff' }}
              >
                {optin.ctaText || 'Continue'}
                {optin.isTestCta && (
                  <span className="absolute -top-2 -right-2 bg-purple-500 text-white text-xs px-1.5 py-0.5 rounded-full">A/B</span>
                )}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Social proof badge */}
      {optin.socialProofEnabled && (
        <div className="absolute bottom-20 left-4 bg-popover border border-border p-3 rounded-lg shadow-lg max-w-[280px] text-sm text-foreground">
          ✅ <strong>{optin.socialProofNames?.[0] || 'Chinedu'} from {optin.socialProofLocations?.[0] || 'Lagos'}</strong>{' '}
          {optin.socialProofMessage || 'just signed up'}
        </div>
      )}

      {/* T&C footer */}
      {optin.showTermsLink && (
        <div className="absolute bottom-0 left-0 right-0 text-center py-3 text-sm">
          <span style={{ color: optin.termsColor || '#888' }}>
            {optin.termsText || 'Subject to our'}{' '}
            <span className="underline cursor-pointer">
              {optin.termsLinkText || 'T&Cs'}
            </span>
          </span>
        </div>
      )}

      {/* Popup tab — clean view, no overlay */}
      {previewTab === 'popup' && optin.popupEnabled && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/40"
          style={{ backgroundColor: optin.backgroundColor || '#7cb342' }}>
          <div className="bg-white p-6 rounded-xl shadow-2xl max-w-[360px] w-full mx-4 text-center">
            {optin.popupTitle && (
              <h3 className="text-xl font-bold mb-3" style={{ color: '#dc2626' }}>
                {optin.popupTitle}
              </h3>
            )}
            {optin.popupMessage && (
              <p className="text-sm text-gray-600 mb-5">{optin.popupMessage}</p>
            )}
            <button
              className="w-full p-3 rounded-lg font-semibold text-white relative"
              style={{ backgroundColor: primaryColor }}
            >
              {optin.popupCtaText || 'Accept Now'}
              {optin.popupIsTestCta && (
                <span className="absolute -top-2 -right-2 bg-purple-500 text-white text-xs px-1.5 py-0.5 rounded-full">A/B</span>
              )}
            </button>
            <p className="text-xs text-gray-400 mt-3">Popup preview</p>
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== OPTIN FUNNEL EDITOR ====================
export const OptinFunnelEditor = ({ config, onChange }) => {
  const optin = config.optin || {};

  const updateOptin = (key, value) => onChange({ ...config, optin: { ...optin, [key]: value } });

  const updateOption = (index, key, value) => {
    const options = [...(optin.options || [])];
    options[index] = { ...options[index], [key]: value };
    updateOptin('options', options);
  };

  const addOption = () => {
    const options = [...(optin.options || [])];
    options.push({ text: `Option ${options.length + 1}`, destinationUrl: '' });
    updateOptin('options', options);
  };

  const removeOption = (index) => {
    updateOptin('options', (optin.options || []).filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-6 p-4 overflow-y-auto h-full">

      {/* Flow Type */}
      <div>
        <label className="block text-sm font-medium mb-2">Flow Type</label>
        <div className="flex gap-2">
          <button
            onClick={() => updateOptin('flowType', '2step')}
            className={`flex-1 p-3 border rounded-lg text-sm ${optin.flowType === '2step' || !optin.flowType ? 'bg-primary text-white border-primary' : ''}`}
          >
            <Layers className="h-4 w-4 mx-auto mb-1" />
            2-Step Optin
          </button>
          <button
            onClick={() => updateOptin('flowType', '1step')}
            className={`flex-1 p-3 border rounded-lg text-sm ${optin.flowType === '1step' ? 'bg-primary text-white border-primary' : ''}`}
          >
            <MousePointerClick className="h-4 w-4 mx-auto mb-1" />
            Single Page
          </button>
        </div>
      </div>

      <hr />

      {/* Appearance */}
      <div className="space-y-3">
        <h4 className="font-semibold text-sm flex items-center gap-2"><Palette className="h-4 w-4" /> Appearance</h4>
        <ColorPicker label="Page Background" value={optin.backgroundColor} onChange={v => updateOptin('backgroundColor', v)} placeholder="#7cb342" />
        <ColorPicker label="Container Background" value={optin.containerBgColor} onChange={v => updateOptin('containerBgColor', v)} placeholder="#ffffff" />
        <Input label="Logo URL" value={optin.logoUrl || ''} onChange={e => updateOptin('logoUrl', e.target.value)} placeholder="https://..." />
      </div>

      <hr />

      {/* Content */}
      <div className="space-y-3">
        <h4 className="font-semibold text-sm flex items-center gap-2"><Type className="h-4 w-4" /> Content</h4>
        <Input label="Title" value={optin.title || ''} onChange={e => updateOptin('title', e.target.value)} placeholder="How much do you need?" />
        <div className="grid grid-cols-2 gap-2">
          <Input label="Title Size" value={optin.titleSize || '20px'} onChange={e => updateOptin('titleSize', e.target.value)} placeholder="20px" />
          <ColorPicker label="Color" value={optin.titleColor} onChange={v => updateOptin('titleColor', v)} />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Subtitle (optional)</label>
          <textarea
            value={optin.subtitle || ''}
            onChange={e => updateOptin('subtitle', e.target.value)}
            className="w-full px-3 py-2 border border-border rounded-lg h-16 text-sm bg-card text-foreground"
            placeholder="Optional subtitle text"
          />
        </div>
        <ColorPicker label="Subtitle Color" value={optin.subtitleColor} onChange={v => updateOptin('subtitleColor', v)} />
      </div>

      <hr />

      {/* Option Buttons (2-step only) */}
      {(optin.flowType === '2step' || !optin.flowType) && (
        <>
          <div className="space-y-3">
            <h4 className="font-semibold text-sm flex items-center gap-2"><List className="h-4 w-4" /> Step 1: Option Buttons</h4>
            <p className="text-xs text-muted-foreground">Users click an option to proceed to Step 2</p>
            {(optin.options || []).map((opt, i) => (
              <div key={i} className="border rounded-lg p-3 space-y-2">
                <div className="flex gap-2 items-center">
                  <input
                    value={opt.text || ''}
                    onChange={e => updateOption(i, 'text', e.target.value)}
                    className="flex-1 px-3 py-2 border border-border rounded text-sm bg-card text-foreground"
                    placeholder={`Option ${i + 1} text`}
                  />
                  {(optin.options || []).length > 1 && (
                    <button onClick={() => removeOption(i)} className="p-1.5 text-red-500 hover:bg-red-50 rounded">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
                <input
                  value={opt.destinationUrl || ''}
                  onChange={e => updateOption(i, 'destinationUrl', e.target.value)}
                  className="w-full px-3 py-1.5 border border-border rounded text-sm bg-card text-foreground"
                  placeholder="Override URL for this option (optional)"
                />
              </div>
            ))}
            <Button size="sm" variant="outline" onClick={addOption}>
              <Plus className="h-3 w-3 mr-1" /> Add Option
            </Button>
            <ColorPicker label="Option Text Color" value={optin.optionTextColor} onChange={v => updateOptin('optionTextColor', v)} />
          </div>

          <hr />

          {/* Step 2 CTA */}
          <div className="space-y-3">
            <h4 className="font-semibold text-sm flex items-center gap-2"><MousePointerClick className="h-4 w-4" /> Step 2: Final CTA</h4>
            <p className="text-xs text-muted-foreground">Shown after user picks an option</p>
            <Input label="CTA Button Text" value={optin.ctaText || ''} onChange={e => updateOptin('ctaText', e.target.value)} placeholder="👉 Continue" />
            <Input label="Destination URL" value={optin.ctaDestinationUrl || ''} onChange={e => updateOptin('ctaDestinationUrl', e.target.value)} placeholder="https://..." />
            <ColorPicker label="CTA Text Color" value={optin.ctaTextColor} onChange={v => updateOptin('ctaTextColor', v)} placeholder="#ffffff" />
            <label className="flex items-center gap-2 p-3 border rounded-lg bg-purple-50">
              <input type="checkbox" checked={optin.isTestCta || false} onChange={e => updateOptin('isTestCta', e.target.checked)} className="rounded" />
              <div>
                <span className="text-sm font-medium">Enable for A/B Testing</span>
                <p className="text-xs text-muted-foreground">Assign this CTA to a test in Projects</p>
              </div>
            </label>
          </div>
        </>
      )}

      {/* Single CTA (1-step) */}
      {optin.flowType === '1step' && (
        <div className="space-y-3">
          <h4 className="font-semibold text-sm flex items-center gap-2"><MousePointerClick className="h-4 w-4" /> Call to Action</h4>
          <Input label="CTA Button Text" value={optin.ctaText || ''} onChange={e => updateOptin('ctaText', e.target.value)} placeholder="👉 Continue" />
          <Input label="Destination URL" value={optin.ctaDestinationUrl || ''} onChange={e => updateOptin('ctaDestinationUrl', e.target.value)} placeholder="https://..." />
          <ColorPicker label="CTA Text Color" value={optin.ctaTextColor} onChange={v => updateOptin('ctaTextColor', v)} placeholder="#ffffff" />
          <label className="flex items-center gap-2 p-3 border rounded-lg bg-purple-50">
            <input type="checkbox" checked={optin.isTestCta || false} onChange={e => updateOptin('isTestCta', e.target.checked)} className="rounded" />
            <div>
              <span className="text-sm font-medium">Enable for A/B Testing</span>
              <p className="text-xs text-muted-foreground">Assign this CTA to a test in Projects</p>
            </div>
          </label>
        </div>
      )}

      <hr />

      {/* Countdown Timer */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="font-semibold text-sm flex items-center gap-2"><Timer className="h-4 w-4" /> Countdown Timer</h4>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={optin.countdownEnabled || false} onChange={e => updateOptin('countdownEnabled', e.target.checked)} className="rounded" />
            <span className="text-sm">Enable</span>
          </label>
        </div>
        {optin.countdownEnabled && (
          <>
            <Input label="Duration (seconds)" type="number" value={optin.countdownDuration || 30} onChange={e => updateOptin('countdownDuration', parseInt(e.target.value) || 30)} />
            <Input label="Stop at (seconds)" type="number" value={optin.countdownStopAt || 2} onChange={e => updateOptin('countdownStopAt', parseInt(e.target.value) || 2)} />
          </>
        )}
      </div>

      <hr />

      {/* Footer */}
      <div className="space-y-3">
        <h4 className="font-semibold text-sm">Footer</h4>
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={optin.showTermsLink || false} onChange={e => updateOptin('showTermsLink', e.target.checked)} className="rounded" />
          <span className="text-sm">Show Terms & Conditions link</span>
        </label>
        {optin.showTermsLink && (
          <>
            <Input label="Footer text (before link)" value={optin.termsText || ''} onChange={e => updateOptin('termsText', e.target.value)} placeholder="Subject to our" />
            <Input label="Link text" value={optin.termsLinkText || ''} onChange={e => updateOptin('termsLinkText', e.target.value)} placeholder="T&Cs" />
            <Input label="Terms URL" value={optin.termsUrl || ''} onChange={e => updateOptin('termsUrl', e.target.value)} placeholder="https://..." />
            <ColorPicker label="Text & Link Color" value={optin.termsColor} onChange={v => updateOptin('termsColor', v)} />
          </>
        )}
      </div>
    </div>
  );
};
